<template>
  <div>
    <PanelSkeleton v-if="store.isLoading('kpis')" shape="card" class="mb-2" />

    <div
      v-for="section in store.isLoading('kpis') ? [] : sections"
      :key="section.title"
      class="mb-2"
    >
      <div
        class="flex align-items-center justify-content-between mb-2"
        style="color: var(--text-color)"
      >
        <span class="text-lg font-medium">{{ section.title }}</span>
        <ExportButton @click="exportSection(section)" />
      </div>
      <div class="tw-flex tw-flex-wrap tw-gap-2">
        <div
          v-for="card in section.cards"
          :key="card.title"
          class="tw-flex-1 tw-basis-56 tw-min-w-0"
        >
          <MetricCard
            :title="card.title"
            :content-str="card.contentStr"
            :card-type="card.cardType"
            :loader="card.loader"
            :info="card.info"
          />
        </div>
      </div>
    </div>

    <div v-if="loadingPanels" class="grid">
      <div v-for="n in 3" :key="n" class="col-12 md:col-6">
        <PanelSkeleton />
      </div>
    </div>

    <div v-if="!loadingPanels" class="grid">
      <div class="col-12 md:col-6">
        <Card>
          <template #title>
            <div class="text-lg font-semibold flex align-items-center">
              Quartile Funnel
              <InfoIcon
                text="Share of snippets played that reached each quarter."
              />
              <ExportButton :disabled="!hasFunnel" @click="exportFunnel" />
            </div>
          </template>
          <template #content>
            <column-chart
              v-if="hasFunnel"
              :data="funnelChart"
              :dataset="{ backgroundColor: FUNNEL_COLORS }"
              suffix="%"
              :max="100"
            />
            <div v-else class="chart-empty text-sm text-bluegray-500">
              {{ SNIPPET_EMPTY_MESSAGE }}
            </div>
          </template>
        </Card>
      </div>
      <div v-if="showsScroll" class="col-12 md:col-6">
        <Card>
          <template #title>
            <div class="text-lg font-semibold flex align-items-center">
              Scroll Direction
              <InfoIcon
                text="Manual moves between snippets. Auto-advance is not counted."
              />
              <ExportButton :disabled="!hasScroll" @click="exportScroll" />
            </div>
          </template>
          <template #content>
            <bar-chart
              v-if="hasScroll"
              :data="scrollChart"
              :dataset="{ backgroundColor: SCROLL_COLORS }"
            />
            <div v-else class="chart-empty text-sm text-bluegray-500">
              {{ SNIPPET_EMPTY_MESSAGE }}
            </div>
          </template>
        </Card>
      </div>
    </div>

    <PanelSkeleton
      v-if="
        store.showsEpisodes &&
        store.isLoading('episodes') &&
        !store.episodes.rows.length
      "
      class="mb-3"
      height="26rem"
    />

    <Card v-else-if="store.showsEpisodes" class="mb-3">
      <template #title>
        <div class="text-lg font-semibold flex align-items-center">
          Episode Engagement
          <InfoIcon text="One row per episode. Sort any column to rank them." />
          <ExportButton
            :disabled="!store.episodes.totalRecords"
            :loading="exportingEpisodes"
            @click="exportEpisodesTable"
          />
        </div>
      </template>
      <template #content>
        <div class="overflow-x-auto">
          <DataTable
            :value="store.episodes.rows"
            :total-records="store.episodes.totalRecords"
            :rows="episodeRows"
            :loading="store.isLoading('episodes')"
            class="p-datatable-striped p-datatable-sm"
            paginator
            lazy
            @page="onEpisodePage"
            @sort="onEpisodePage"
          >
            <Column
              v-for="col in episodeColumns"
              :key="col.field"
              :field="col.field"
              :header="col.header"
              sortable
            >
              <template #body="{ data }">
                {{ col.format(data[col.field]) }}
              </template>
            </Column>
            <template #empty>
              <span class="text-sm text-bluegray-500">{{
                SNIPPET_EMPTY_MESSAGE
              }}</span>
            </template>
          </DataTable>
        </div>
      </template>
    </Card>

    <div v-if="!loadingPanels" class="grid">
      <div v-for="chart in charts" :key="chart.title" class="col-12 md:col-6">
        <Card>
          <template #title>
            <div class="text-lg font-semibold flex align-items-center">
              {{ chart.title }}
              <InfoIcon v-if="chart.info" :text="chart.info" />
              <ExportButton
                :disabled="!chart.hasData"
                @click="exportChart(chart)"
              />
            </div>
          </template>
          <template #content>
            <column-chart
              v-if="chart.hasData"
              :data="chart.series"
              :colors="chart.colors"
            />
            <div v-else class="chart-empty text-sm text-bluegray-500">
              {{ SNIPPET_EMPTY_MESSAGE }}
            </div>
          </template>
        </Card>
      </div>
    </div>

    <PanelSkeleton
      v-if="store.isLoading('daily') && !store.daily.rows.length"
      class="my-3"
      height="26rem"
    />

    <Card v-else class="my-3">
      <template #title>
        <div class="text-lg font-semibold flex align-items-center">
          Engagement Daily
          <ExportButton
            :disabled="!store.daily.totalRecords"
            :loading="exportingDaily"
            @click="exportDailyTable"
          />
        </div>
      </template>
      <template #content>
        <div class="overflow-x-auto">
          <DataTable
            :value="store.daily.rows"
            :total-records="store.daily.totalRecords"
            :rows="dailyRows"
            :loading="store.isLoading('daily')"
            class="p-datatable-striped p-datatable-sm"
            paginator
            lazy
            @page="onDailyPage"
            @sort="onDailyPage"
          >
            <Column
              v-for="col in dailyColumns"
              :key="col.field"
              :field="col.field"
              :header="col.header"
              sortable
            >
              <template #body="{ data }">
                {{ col.format(data[col.field]) }}
              </template>
            </Column>
            <template #empty>
              <span class="text-sm text-bluegray-500">{{
                SNIPPET_EMPTY_MESSAGE
              }}</span>
            </template>
          </DataTable>
        </div>
      </template>
    </Card>
  </div>
</template>

<script setup>
import { computed, ref } from "vue";
import Card from "primevue/card";
import MetricCard from "@/components/SnippetDashboard/MetricCard.vue";
import PanelSkeleton from "@/components/SnippetDashboard/PanelSkeleton.vue";
import InfoIcon from "@/components/SnippetDashboard/InfoIcon.vue";
import ExportButton from "@/components/SnippetDashboard/ExportButton.vue";
import { formatMoney } from "@/helpers";
import { SNIPPET_EMPTY_MESSAGE } from "@/consts";
import { useSnippetEngagementStore } from "@/stores/snippetEngagement";
import { downloadCsv } from "@/utils/csvExport";

const store = useSnippetEngagementStore();

const CARD_TYPE = "minimal";

const CARD_INFO = {
  "Total Unmutes": "Sound turned on. Counted every time.",
  "Unmute Rate":
    "Total Unmutes ÷ Viewable Widget Loads. Can exceed 100% — one viewer can unmute more than once.",
  Completions: "Reached the end of the snippet with sound on.",
  "Completion Rate": "Completions ÷ Snippets Played.",
  "Snippets Played":
    "Snippets that played with sound. One unmute can carry through the rest of the playlist.",
  "Floating Player Opens":
    "The floating player was expanded. Counted every time.",
  "Share Rate": "Shares ÷ Snippets Played.",
  "Page Loads":
    "Pages that loaded the snippet script. One per page, however many widgets it holds.",
  "Widget Loads":
    "Widgets that requested content from the server. One per widget on the page.",
  "Viewable Widget Loads":
    "A widget was continuously visible for 3 seconds. Counted once per widget.",
  Viewability: "Viewable Widget Loads ÷ Widget Loads.",
  Shares: "The share button copied the snippet link.",
};

const count = (value) => formatMoney(value, "decimal");

const percent = (value) =>
  value === null ? "—" : `${(value * 100).toFixed(2)}%`;

const kpiLoader = computed(() => (store.isLoading("kpis") ? 1 : 0));

const sectionSpecs = computed(() => [
  {
    title: "Content Performance",
    cards: [
      {
        title: "Total Unmutes",
        metricKey: "snp_unmute",
        cardType: CARD_TYPE,
        contentStr: count(store.counts.snp_unmute),
        loader: kpiLoader.value,
      },
      {
        title: "Unmute Rate",
        metricKey: "snp_unmute_rate",
        cardType: CARD_TYPE,
        contentStr: percent(store.rates.snp_unmute_rate),
        loader: kpiLoader.value,
      },
      {
        title: "Completions",
        metricKey: "snp_q4",
        cardType: CARD_TYPE,
        contentStr: count(store.counts.snp_q4),
        loader: kpiLoader.value,
      },
      {
        title: "Completion Rate",
        metricKey: "snp_completion_rate",
        cardType: CARD_TYPE,
        contentStr: percent(store.rates.snp_completion_rate),
        loader: kpiLoader.value,
      },
      {
        title: "Snippets Played",
        metricKey: "snp_play",
        cardType: CARD_TYPE,
        contentStr: count(store.counts.snp_play),
        loader: kpiLoader.value,
      },
    ],
  },
  {
    title: "Engagement",
    cards: [
      {
        title: "Floating Player Opens",
        metricKey: "snp_fab_open",
        cardType: CARD_TYPE,
        contentStr: count(store.counts.snp_fab_open),
        loader: kpiLoader.value,
      },
      {
        title: "Shares",
        metricKey: "snp_share",
        cardType: CARD_TYPE,
        contentStr: count(store.counts.snp_share),
        loader: kpiLoader.value,
      },
      {
        title: "Share Rate",
        metricKey: "snp_share_rate",
        cardType: CARD_TYPE,
        contentStr: percent(store.rates.snp_share_rate),
        loader: kpiLoader.value,
      },
      {
        title: "Page Loads",
        metricKey: "snp_widget_viewed",
        cardType: CARD_TYPE,
        contentStr: count(store.counts.snp_widget_viewed),
        loader: kpiLoader.value,
      },
      {
        title: "Widget Loads",
        metricKey: "snp_widget_loads",
        cardType: CARD_TYPE,
        contentStr: count(store.counts.snp_widget_loads),
        loader: kpiLoader.value,
      },
      {
        title: "Viewable Widget Loads",
        metricKey: "snp_in_view_3s",
        cardType: CARD_TYPE,
        contentStr: count(store.counts.snp_in_view_3s),
        loader: kpiLoader.value,
      },
      {
        title: "Viewability",
        metricKey: "snp_viewability",
        cardType: CARD_TYPE,
        contentStr: percent(store.rates.snp_viewability),
        loader: kpiLoader.value,
      },
    ],
  },
]);

const sections = computed(() =>
  sectionSpecs.value
    .map((section) => ({
      ...section,
      cards: section.cards
        .filter((card) => store.shows(card.metricKey))
        .map((card) => ({
          ...card,
          info: CARD_INFO[card.title] ?? "",
        })),
    }))
    .filter((section) => section.cards.length)
);

const toChartData = (pairs) => pairs.map(([label, value]) => [label, value]);

const MILESTONE_LABELS = {
  snp_q1: "Q1",
  snp_q2: "Q2",
  snp_q3: "Q3",
  snp_q4: "Q4",
};

const FUNNEL_COLORS = ["#2f6fb5", "#3f9e8c", "#e0a13a", "#d1563f"];

const SURFACE_LABELS = {
  snp_unmute: "Unmute",
  snp_mute: "Mute",
  snp_scroll_left: "Scroll Left",
  snp_scroll_right: "Scroll Right",
  snp_share: "Share",
  snp_cta_listen_on: "RSS",
  snp_cta_spotify: "Spotify",
  snp_cta_apple: "Apple",
  snp_listen_full_episode: "Full Episode",
  snp_rating: "Rating",
};
const SURFACE_COLORS = ["#2f6fb5", "#e8703a"];
const SCROLL_COLORS = ["#6d5bd0", "#3f9e8c"];
const CTA_COLOR = "#e8703a";
const INTENT_COLOR = "#e8703a";
const END_SCREEN_COLOR = "#2f6fb5";

const loadingPanels = computed(
  () =>
    store.isLoading("kpis") ||
    store.isLoading("funnel") ||
    store.isLoading("cta") ||
    store.isLoading("surfaces")
);

const showsScroll = computed(
  () => store.shows("snp_scroll_left") || store.shows("snp_scroll_right")
);

const hasFunnel = computed(() =>
  store.funnel.milestones.some((step) => step.share !== null)
);

const scrollChart = computed(() => [
  ["Left", store.counts.snp_scroll_left],
  ["Right", store.counts.snp_scroll_right],
]);

const hasScroll = computed(
  () => store.counts.snp_scroll_left > 0 || store.counts.snp_scroll_right > 0
);

const funnelChart = computed(() =>
  store.funnel.milestones.map((step) => [
    MILESTONE_LABELS[step.milestone] ?? step.milestone,
    step.share === null ? 0 : Number((step.share * 100).toFixed(2)),
  ])
);

const episodeRows = ref(10);

const text = (value) => value || "—";

const episodeColumns = [
  { field: "showTitle", header: "Show", format: text },
  { field: "episodeTitle", header: "Episode", format: text },
  { field: "host", header: "Host", format: text },
  { field: "snp_unmute", header: "Unmutes", format: count },
  { field: "snp_play", header: "Snippets Played", format: count },
  { field: "snp_q4", header: "Completions", format: count },
];

const onEpisodePage = (event) => {
  episodeRows.value = event.rows ?? episodeRows.value;

  store.loadEpisodes({
    page: event.page ?? 0,
    rows: episodeRows.value,
    sortField: event.sortField,
    sortOrder: event.sortOrder,
  });
};

const dailyRows = ref(10);

const DAILY_COLUMNS = {
  date: { header: "Date", format: (value) => value },
  snp_widget_viewed: { header: "Page Loads", format: count },
  snp_in_view_3s: { header: "Viewable Widget Loads", format: count },
  snp_play: { header: "Plays", format: count },
  snp_q4: { header: "Completions", format: count },
  snp_unmute: { header: "Unmutes", format: count },
  snp_scroll_left: { header: "Scroll Left", format: count },
  snp_scroll_right: { header: "Scroll Right", format: count },
  snp_fab_open: { header: "Fab Opens", format: count },
  snp_share: { header: "Shares", format: count },
  snp_end_shown: { header: "End Shown", format: count },
  snp_end_dismissed: { header: "End Dismissed", format: count },
};

const dailyColumns = computed(() =>
  (store.daily.columns ?? [])
    .filter((field) => DAILY_COLUMNS[field])
    .map((field) => ({ field, ...DAILY_COLUMNS[field] }))
);

const onDailyPage = (event) => {
  dailyRows.value = event.rows ?? dailyRows.value;

  store.loadDaily({
    page: event.page ?? 0,
    rows: dailyRows.value,
    sortField: event.sortField,
    sortOrder: event.sortOrder,
  });
};

const charts = computed(() => {
  const ctaPairs = [
    ["RSS", store.cta.snp_cta_listen_on],
    ["Spotify", store.cta.snp_cta_spotify],
    ["Apple", store.cta.snp_cta_apple],
  ];
  const intentPairs = [
    ["Wants more", store.ctaIntent.wants_more],
    ["Listener apps", store.ctaIntent.listener_apps],
  ];
  const surfaceSeries = [
    ["Widget", "inline"],
    ["Floating Player", "fab"],
  ].map(([name, key]) => ({
    name,
    data: store.surfaces.map((row) => [
      SURFACE_LABELS[row.action] ?? row.action,
      row[key],
    ]),
  }));
  const endPairs = [
    ["Shown", store.counts.snp_end_shown],
    ["Dismissed", store.counts.snp_end_dismissed],
  ];

  return [
    {
      title: "CTA Clicks by Type",
      shows: store.showsCtaByType,
      loaderKey: "cta",
      series: [{ name: "Clicks", data: toChartData(ctaPairs) }],
      colors: [CTA_COLOR],
      info: "Clicks through to a listening destination. YouTube is not tracked yet.",
      hasData: ctaPairs.some(([, value]) => value > 0),
    },
    {
      title: "CTA Intent",
      shows: true,
      loaderKey: "cta",
      series: [{ name: "Clicks", data: toChartData(intentPairs) }],
      colors: [INTENT_COLOR],
      info: "'Wants more' goes to the full episode on JamX. 'Listener apps' goes out to Spotify, Apple or RSS.",
      hasData: intentPairs.some(([, value]) => value > 0),
    },
    {
      title: "Widget vs Floating Player",
      shows: true,
      loaderKey: "surfaces",
      series: surfaceSeries,
      colors: SURFACE_COLORS,
      info: "The same actions, split by where they were taken. Totals elsewhere count both.",
      hasData: store.surfaces.some((row) => row.inline > 0 || row.fab > 0),
    },
    {
      title: "End Screen Interactions",
      shows: store.shows("snp_end_shown") || store.shows("snp_end_dismissed"),
      loaderKey: "kpis",
      series: [{ name: "Events", data: toChartData(endPairs) }],
      colors: [END_SCREEN_COLOR],
      info: "How often the end screen appeared, and how often it was dismissed.",
      hasData: endPairs.some(([, value]) => value > 0),
    },
  ].filter((chart) => chart.shows);
});

const exportSection = (section) => {
  downloadCsv(
    `snippet-kpis-${section.title.toLowerCase().replace(/\s+/g, "-")}.csv`,
    section.cards.map((card) => ({
      metric: card.title,
      value: card.contentStr === "—" ? "" : card.contentStr,
    })),
    [
      { header: "Metric", field: "metric" },
      { header: "Value", field: "value" },
    ]
  );
};

const exportFunnel = () => {
  downloadCsv(
    "snippet-engagement-funnel.csv",
    store.funnel.milestones.map((step) => ({
      milestone: MILESTONE_LABELS[step.milestone] ?? step.milestone,
      count: step.count,
      sharePercent: step.share === null ? "" : (step.share * 100).toFixed(2),
    })),
    [
      { header: "Milestone", field: "milestone" },
      { header: "Count", field: "count" },
      { header: "Share %", field: "sharePercent" },
    ]
  );
};

const exportScroll = () => {
  downloadCsv(
    "snippet-engagement-scroll.csv",
    scrollChart.value.map(([direction, count]) => ({ direction, count })),
    [
      { header: "Direction", field: "direction" },
      { header: "Count", field: "count" },
    ]
  );
};

const exportChart = (chart) => {
  const [first, ...rest] = chart.series;
  const valueHeader = (series) => (rest.length ? series.name : "Value");

  downloadCsv(
    `snippet-engagement-${chart.title.toLowerCase().replace(/\s+/g, "-")}.csv`,
    first.data.map(([label, value], index) => ({
      label,
      value,
      ...Object.fromEntries(
        rest.map((series, n) => [`value${n + 1}`, series.data[index]?.[1]])
      ),
    })),
    [
      { header: "Label", field: "label" },
      { header: valueHeader(first), field: "value" },
      ...rest.map((series, n) => ({
        header: series.name,
        field: `value${n + 1}`,
      })),
    ]
  );
};

const exportingEpisodes = ref(false);

const exportEpisodesTable = async () => {
  exportingEpisodes.value = true;
  try {
    const data = await store.exportEpisodes();
    if (!data?.rows?.length) return;

    downloadCsv(
      "snippet-engagement-episodes.csv",
      data.rows,
      episodeColumns.map((col) => ({ header: col.header, field: col.field }))
    );
  } finally {
    exportingEpisodes.value = false;
  }
};

const exportingDaily = ref(false);

const exportDailyTable = async () => {
  exportingDaily.value = true;
  try {
    const data = await store.exportDaily();
    if (!data?.rows?.length) return;

    downloadCsv(
      "snippet-engagement-daily.csv",
      data.rows,
      dailyColumns.value.map((column) => ({
        header: column.header,
        field: column.field,
      }))
    );
  } finally {
    exportingDaily.value = false;
  }
};
</script>

<style scoped>
.chart-empty {
  display: flex;
  align-items: center;
  min-height: 300px;
}

:deep(.p-datatable .p-column-title) {
  white-space: nowrap;
}
</style>
